import { postPreExchangeOperation } from "../../../app/api/exchangeOffer";
import { getBooking } from "../../../app/api/getBooking";
import { BaseAction } from "../../../core/actions/BaseAction";
import { _eventBus } from "../../../core/events/EventBus";
import { determinePassengerType, isActionRunSuccessfully } from "../../../Lib/Utils/validationUtil";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { clearBookingFlowAtoms } from "../../../Orchestrator/Recoils/clearBookingFlowAtoms";
import { IDeterminePassenger, IPreExchangeOperationRequest, IBookedTrips, IPreExchangeOperationResponse, IBookingResponse, ISelectedPassenger, IBookedOffer, ITrip } from "../../../Orchestrator/Types/Index";
import { ICalculatePassengerListByPriceActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/BookItAgainActionParams";
import { calculateAlreadyPaidAddonsAction } from "../Addons/CalculateAlreadyPaidAddons";
import { cleanupOrphanAddonsAction } from "../Addons/CleanupOrphanAddonsAction";
import { prepareBookingSummaryAction } from "../Checkout/Summary/PrepareBookingSummaryAction";
import { getAllOfferWithoutExchangeAction } from "../CommonActions/GetAllOfferWithoutExchangeAction";
import { getAllTripWithoutExchangeAction } from "../CommonActions/GetAllTripWithoutExchangeAction";
import { calculatePassengerListByPriceAction } from "../Search/CalculatePassengerListByPriceAction";
import { extractCarriedOverAddons } from "./extractCarriedOverAddons";

export interface IUpgradeToPremiumParams {
    trip: IBookedTrips;
    bookingId?: string;
    upgradeOutboundOffers?: any;
    upgradeInboundOffers?: any;
}

class UpgradeToPremiumAction extends BaseAction<string, IUpgradeToPremiumParams> {
    // Coalesces accidental double-fires (e.g. button double-click). Safe here
    // because every concurrent dispatch comes from the same click handler with
    // identical params (booking id + offers from the same component instance).
    // If a future caller dispatches concurrently with DIFFERENT params, the
    // second caller's params would be silently ignored — convert to per-call
    // instances (`new UpgradeToPremiumAction()`) before that lands.
    protected override deduplicate = true;

    private handleDirectCheckout = async (trip: IBookedTrips, upgradeOutboundOffers: any, upgradeInboundOffers: any, bookingId: string) => {
        try {
            localStorage.removeItem("isUpgradeToPremiumFlow");
            // Prefer outbound offers, fallback to inbound offers
            const offers = (upgradeOutboundOffers?.exchangeOffers?.length > 0)
                ? upgradeOutboundOffers
                : (upgradeInboundOffers?.exchangeOffers?.length > 0)
                    ? upgradeInboundOffers
                    : null;
            if (!offers || !offers.exchangeOffers || offers.exchangeOffers.length === 0) return;

            // Set exchange offers atom
            const offerIds: string[] = Array.from(new Set(offers.exchangeOffers.flatMap((offer: any) => {
                const ids = offer?.offerId;
                if (Array.isArray(ids)) {
                    return ids;
                }
                return ids ? [ids] : [];
            })));
            updateRecoilState<string[]>("exchangeOffersAtom", offerIds);

            // Prepare pre-exchange operation
            const payloadPreExchangeOperation: IPreExchangeOperationRequest = {
                exchangeOffers: offerIds.map((offerId: string) => ({
                    offerId,
                    placeSelections: null,
                    passengerRefs: offers.passengers.map((p: any) => p.externalRef),
                    throughTicketTags: null
                }))
            };

            const getBookingResponse: any = await getBooking(bookingId);
            if (getBookingResponse.status === 'ok' && getBookingResponse.answer) {
                let bookingResponse: IBookingResponse = getBookingResponse.answer;

                // Strip orphan PREBOOKED ancillaries from a prior abandoned
                // manage-seating / addons session. cleanupOrphanAddonsAction
                // reads bookingResponseAtom; seed it first so the scan sees
                // the freshly-fetched booking, then re-read after cleanup so
                // the rest of this flow uses the cleaned state.
                updateRecoilState("bookingResponseAtom", bookingResponse);
                await cleanupOrphanAddonsAction.execute();
                const refreshed = getRecoilStateValue<IBookingResponse>("bookingResponseAtom");
                if (refreshed?.booking) {
                    bookingResponse = refreshed;
                }

                /**
                * Manage Addons into booking for booking change trip date.
                * Checking that existing booking have addons or not.
                * If have addons then we need to prepare payment summary for already paid addons amount.
                * That already paid addons amount will be show into payment summary section.
                */
                let allBookedOfferWithoutExchange: IBookedOffer[] = []
                getAllOfferWithoutExchangeAction.setParams(bookingResponse.booking.bookedOffers)
                await getAllOfferWithoutExchangeAction.execute();
                if (isActionRunSuccessfully(getAllOfferWithoutExchangeAction)) {
                    allBookedOfferWithoutExchange = getAllOfferWithoutExchangeAction?.result || []
                }
                let allBookedTripsWithoutExchange: ITrip[] = []
                getAllTripWithoutExchangeAction.setParams({ bookedOffers: allBookedOfferWithoutExchange, trips: bookingResponse.booking.trips })
                await getAllTripWithoutExchangeAction.execute();
                if (isActionRunSuccessfully(getAllTripWithoutExchangeAction)) {
                    allBookedTripsWithoutExchange = getAllTripWithoutExchangeAction?.result || []
                }
                calculateAlreadyPaidAddonsAction.setParams({
                    allBookedOfferWithoutExchange: allBookedOfferWithoutExchange,
                    allBookedTripWithoutExchange: allBookedTripsWithoutExchange
                })
                await calculateAlreadyPaidAddonsAction.execute();
            }

            const preExchangeResponse: any = await postPreExchangeOperation(payloadPreExchangeOperation, bookingId);

            if (preExchangeResponse.status === 'ok' && preExchangeResponse.answer) {
                const bookingResponse: IPreExchangeOperationResponse = preExchangeResponse.answer;
                if (bookingResponse.exchangeOperation) {
                    sessionStorage.setItem("exchangeOperationId", bookingResponse.exchangeOperation.id)
                    sessionStorage.setItem("exchangeBookingId", bookingId)
                    //Get Booking by Booking Id
                    const getBookingResponse: any = await getBooking(bookingId);
                    if (getBookingResponse.status === 'ok' && getBookingResponse.answer) {
                        const bookingResponse: IBookingResponse = getBookingResponse.answer;
                        // Update Recoil state with the booking response
                        updateRecoilState('quickExchangeBookingResponseAtom', bookingResponse);
                        updateRecoilState('bookingResponseAtom', bookingResponse);

                        // Surface existing booking ancillaries (now EXCHANGE_ONGOING on the
                        // pre-exchange offers) as read-only "carried over" addons so the
                        // upgrade checkout shows them without letting the user edit/repurchase.
                        updateRecoilState("carriedOverAddonsAtom", extractCarriedOverAddons(bookingResponse.booking));

                        const passengerList: ISelectedPassenger[] = bookingResponse.booking?.passengers.map(passenger => {
                            const passengerObject: ISelectedPassenger = {
                                id: passenger.id,
                                firstName: passenger?.detail?.firstName || "",
                                lastName: passenger?.detail?.lastName || "",
                                email: passenger?.detail?.email || "",
                                mobile: "",
                                remember: false,
                                age: passenger.age,
                                seatingDetails: null,
                                cards: passenger.cards || [],
                                prmNeeds: passenger.prmNeeds
                            }
                            return passengerObject
                        });
                        let filteredPassengers: ISelectedPassenger[] = [];
                        const uniqueSelectedIds = offers?.passengers?.map((x: any) => x.id);
                        if (uniqueSelectedIds.length > 0) {
                            filteredPassengers = passengerList.filter(p => uniqueSelectedIds.includes(p.id));
                        }

                        const passengersObject: { [key: string]: number } = {};
                        filteredPassengers.forEach((passenger) => {
                            const determinePassenger: IDeterminePassenger = {
                                age: passenger.age || 0,
                                cards: passenger?.cards || [],
                                prmNeeds: ((passenger?.prmNeeds || []).length > 0 && passenger?.prmNeeds) ? passenger?.prmNeeds[0] : ""
                            }
                            const passengerType = determinePassengerType(determinePassenger);

                            // Initialize the count for the passenger type if not already present
                            if (!passengersObject[passengerType]) {
                                passengersObject[passengerType] = 0;
                            }

                            // Increment the count for this passenger type
                            passengersObject[passengerType] += 1;
                        });

                        updateRecoilState("searchStateAtom", { passengers: passengersObject });


                        const filteredBookingPassengers = bookingResponse.booking?.passengers.filter(p => uniqueSelectedIds.includes(p.id)) || [];
                        updateRecoilState("passengerStateAtom", filteredBookingPassengers);

                        const addonsCreditSummary = getRecoilStateValue("changeTripAlreadyPaidAddonsAtom") 
                        // INVARIANT: an upgrade summary build MUST pass a
                        // selectedPassengerForCancel / selectedPassengerForCancelInBound
                        // array (the ternary below always passes exactly one). It is
                        // load-bearing: PrepareBookingSummaryAction only runs its
                        // preparePaymentSummary(isUpgrade=true) pass inside those
                        // sub-branches, and that pass persists `upgradeBaseFareTotal`.
                        // If both were ever omitted, that field is never set and
                        // mergeUpgradePaymentSummary falls back to its fragile
                        // `total - addons - seats` derivation (the upgrade addon
                        // total-drift bug).
                        prepareBookingSummaryAction.setParams({
                            ...bookingResponse.booking,
                            changeTripAlreadyPaidAddons: addonsCreditSummary || {},
                            isUpgrade: true,
                            ...(offers === upgradeOutboundOffers
                                ? { selectedPassengerForCancel: uniqueSelectedIds }
                                : { selectedPassengerForCancelInBound: uniqueSelectedIds }),
                        })
                        await prepareBookingSummaryAction.execute()
                        if (offers === upgradeOutboundOffers) {
                            updateRecoilState("selectedPassengerForCancelAtom", uniqueSelectedIds);
                        } else if (offers === upgradeInboundOffers) {
                            updateRecoilState("selectedPassengerForCancelInBoundAtom", uniqueSelectedIds);
                        }
                        // const params: ICalculatePassengerListByPriceActionParams = {
                        //     booking: bookingResponse.booking,
                        //     screen: "",
                        //     passengerSpecificationsTypes: null
                        // }
                        // calculatePassengerListByPriceAction.setParams(params)
                        // await calculatePassengerListByPriceAction.execute();

                        updateRecoilState("paymentStateAtom", {
                            // Left unset so <Payment> can preselect the customer's default saved
                            // card — it only preselects while no method is set (Payment.tsx).
                            method: null,
                            total: 0,
                        });

                        updateRecoilState("isChangeDateStateAtom", true);
                        updateRecoilState("isQuickExchangeStateAtom", true)
                        updateRecoilState("isUpgradeToPremiumStateAtom", true);
                        localStorage.setItem("isUpgradeToPremiumFlow", "true");

                        // Clear upgrade-specific atoms before navigating to checkout to prevent flash of old content
                        updateRecoilState("upgradeOutboundOffersAtom", null);
                        updateRecoilState("upgradeInboundOffersAtom", null);

                        // Reset addon state before /checkout mount so its useEffect doesn't emit
                        // GetBookingOffersAddOns before the new CheckoutFlow registers a subscriber,
                        // and stale legId-keyed meal data is cleared.
                        updateRecoilState("addonFlowStartedAtom", false);
                        updateRecoilState("mealPreferencesResponseAtom", {});

                        _eventBus.emit({
                            eventName: "ROUTE_CHANGE",
                            data: "/checkout"
                        })
                    }
                }
            }
        } catch (error) {
            console.error("handleDirectCheckout error:", error);
        }
    };

    override async run(): Promise<string> {
        let response: string = "fail"
        try {
            const { trip, upgradeOutboundOffers, upgradeInboundOffers, bookingId } = this.params!;
            if (bookingId) {
                // Wipe stale flow state from any prior aborted checkout before
                // seeding this flow's writes (see clearBookingFlowAtoms doc).
                clearBookingFlowAtoms();
                const hasOutbound = upgradeOutboundOffers && upgradeOutboundOffers.passengers?.length === 1;
                const hasInbound = upgradeInboundOffers && upgradeInboundOffers.passengers?.length === 1;

                const isOneDirectionWithOnePassenger = (hasOutbound && !hasInbound) || (!hasOutbound && hasInbound);

                if (isOneDirectionWithOnePassenger) {
                    // Direct checkout flow for one way, one passenger
                    await this.handleDirectCheckout(trip, upgradeOutboundOffers, upgradeInboundOffers, bookingId);
                } else {
                    // Set upgrade offers in atoms before navigation to ensure page has data to display
                    // This prevents flash of old/stale content from previous bookings
                    updateRecoilState("upgradeOutboundOffersAtom", upgradeOutboundOffers || null);
                    updateRecoilState("upgradeInboundOffersAtom", upgradeInboundOffers || null);

                    // Navigate to upgrade premium page for other cases
                    _eventBus.emit({
                        eventName: "ROUTE_CHANGE",
                        data: `/bookings/upgrade-premium?bookingid=${bookingId}`
                    });
                }
                response = "success";
            }
        } catch (error) {
            console.error("UpgradeToPremiumAction error:", error);
        }
        return response;
    }
}

export const upgradeToPremiumAction = new UpgradeToPremiumAction();
